import { FC, MouseEventHandler } from "react";
import { cn } from "../lib/utils";

const colorMap = {
	red: "bg-red-50 border-red-300 text-red-700",
	orange: "bg-orange-50 border-orange-300 text-orange-700",
	yellow: "bg-amber-50 border-amber-300 text-amber-700",
	green: "bg-green-50 border-green-300 text-green-700",
	blue: "bg-blue-50 border-blue-300 text-blue-700",
	teal: "bg-teal-50 border-teal-300 text-teal-700",
	purple: "bg-purple-50 border-purple-300 text-purple-700",
	gray: "bg-neutral-100 border-neutral-300 text-neutral-700",
};

const hoverColorMap: { [key in keyof typeof colorMap]: string } = {
	red: "hover:bg-red-100 hover:border-red-500",
	orange: "hover:bg-orange-100 hover:border-orange-500",
	yellow: "hover:bg-amber-100 hover:border-amber-500",
	green: "hover:bg-green-100 hover:border-green-500",
	blue: "hover:bg-blue-100 hover:border-blue-500",
	teal: "hover:bg-teal-100 hover:border-teal-500",
	purple: "hover:bg-purple-100 hover:border-purple-500",
	gray: "hover:bg-neutral-200 hover:border-neutral-500",
};

export type TagColor = keyof typeof colorMap;

// For testing
export const colorKeys = Object.keys(colorMap) as TagColor[];

export type TagProps = {
	color?: TagColor;
	iconOnly?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	border?: boolean;
	oval?: boolean;
	children: React.ReactNode;
	className?: string;
	testId?: string;
};

export const Tag: FC<TagProps> = ({
	children,
	color,
	iconOnly,
	onClick,
	border,
	oval,
	className,
	testId,
}) => {
	const styles = cn(
		"min-h-[22px] flex items-center justify-start rounded-full text-xs",
		color ? colorMap[color] : colorMap.gray,
		iconOnly ? "h-[22px] w-[22px] justify-center" : "px-[10px] py-[5px] max-w-fit",
		onClick && `cursor-pointer ${color ? hoverColorMap[color] : hoverColorMap.gray}`,
		border && "border",
		oval && "rounded-lg",
		className,
	);

	return onClick ? (
		<button data-testid={testId ?? "clickable-tag"} className={styles} onClick={onClick}>
			{children}
		</button>
	) : (
		<div data-testid="tag" className={styles}>
			{children}
		</div>
	);
};
